import React, { useState, useEffect, useRef } from 'react';
import SectionCard from './SectionCard';
import { useLanguage } from '../context/LanguageContext';
import { FileText, Users, UserCheck, Briefcase, Globe } from 'lucide-react';

// Animated counter hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

const StatCard = ({ icon: Icon, value, label, suffix = '', color }) => {
  const { count, ref } = useCountUp(value, 2000);
  
  return (
    <div ref={ref} className="stat-card" style={{ '--accent-color': color }}>
      <div className="stat-icon">
        <Icon size={32} />
      </div>
      <div className="stat-value">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

const ResultsSection = ({ isDoorOpen }) => {
  const { t, language } = useLanguage();

  const stats = [
    {
      icon: FileText,
      value: 13,
      label: language === 'fr' 
        ? "Rapports d'évaluation en santé mentale" 
        : "Mental health assessment reports",
      color: '#3b82f6'
    },
    {
      icon: Users,
      value: 3450,
      label: language === 'fr' 
        ? "Personnes reçues en thérapie" 
        : "People received in therapy",
      color: '#10b981'
    },
    {
      icon: UserCheck,
      value: 44,
      label: language === 'fr' 
        ? "Personnes supervisées (hiérarchique)" 
        : "People supervised (hierarchical)",
      color: '#8b5cf6'
    },
    {
      icon: UserCheck,
      value: 172,
      label: language === 'fr' 
        ? "Personnes en supervision (coordination)" 
        : "People in supervision (coordination)",
      color: '#f59e0b'
    },
    {
      icon: Briefcase,
      value: 6,
      label: language === 'fr' 
        ? "Projets en santé mentale gérés" 
        : "Mental health projects managed",
      color: '#ef4444'
    }
  ];

  const countries = [
    { name: "Burkina Faso", flag: "🇧🇫" },
    { name: "Canada", flag: "🇨🇦" },
    { name: language === 'fr' ? "États-Unis" : "United States", flag: "🇺🇸" }
  ];

  return (
    <SectionCard
      id="resultats"
      icon="📊"
      title={t.results?.title || (language === 'fr' ? "Résultats & Impact" : "Results & Impact")}
      subtitle={t.results?.subtitle || (language === 'fr' 
        ? "Chiffres clés illustrant mon parcours et mon impact dans le domaine de la santé mentale."
        : "Key figures illustrating my journey and impact in the mental health field.")}
      isDoorOpen={isDoorOpen}
    >
      <div className="results-container">
        {/* Stats Grid */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              icon={stat.icon}
              value={stat.value}
              label={stat.label}
              color={stat.color}
            />
          ))}
        </div>

        {/* Countries Section */}
        <div className="countries-section">
          <div className="countries-header">
            <Globe size={24} />
            <h3>{language === 'fr' ? "Pays d'intervention" : "Countries of Intervention"}</h3>
          </div>
          <div className="countries-list">
            {countries.map((country, index) => (
              <div key={index} className="country-badge">
                <span className="country-flag">{country.flag}</span>
                <span className="country-name">{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default ResultsSection;
